/**
 * nexus-agents/cli-adapters - Circuit Breaker Implementation
 *
 * Implements the circuit breaker pattern to handle CLI failures gracefully
 * and prevent cascade failures in the multi-CLI mesh.
 *
 * (Source: Issue #81 - Circuit breaker for CLI failures)
 * (Source: Martin Fowler's Circuit Breaker pattern)
 */

import type { Result } from '../core/index.js';
import { getErrorMessage, err, ok, getTimeProvider } from '../core/index.js';

import { ErrorCode, type ModelError } from '../core/errors.js';

import type { CliName, ObservedArmId, CliErrorCode } from './types.js';
import { isCliName, observedArmDisplaySlot } from './types.js';
import {
  CircuitError,
  CircuitErrorCode,
  DEFAULT_CIRCUIT_BREAKER_CONFIG,
  categorizeError,
  type CircuitState,
  type FailureCategory,
  type CircuitBreakerConfig,
  type CircuitBreakerSnapshot,
  type CircuitStateChangeEvent,
  type CircuitStateChangeListener,
  type ICircuitBreaker,
} from './circuit-breaker-types.js';

// Re-export all types for convenience
export {
  CircuitError,
  CircuitErrorCode,
  DEFAULT_CIRCUIT_BREAKER_CONFIG,
  categorizeError,
  type CircuitState,
  type FailureCategory,
  type CircuitBreakerConfig,
  type CircuitBreakerSnapshot,
  type CircuitStateChangeEvent,
  type CircuitStateChangeListener,
  type ICircuitBreaker,
} from './circuit-breaker-types.js';

// ============================================================================
// Circuit Breaker Implementation
// ============================================================================

/**
 * Circuit breaker implementation for CLI adapters.
 *
 * Provides protection against cascading failures by:
 * 1. Tracking failure counts
 * 2. Opening circuit when threshold exceeded
 * 3. Allowing gradual recovery through half-open state
 */
export class CliCircuitBreaker implements ICircuitBreaker {
  private state: CircuitState = 'closed';
  private failureCount = 0;
  private successCount = 0;
  private lastFailureTime: number | null = null;
  private lastStateChange: number;
  private halfOpenRequests = 0;
  private readonly listeners: Set<CircuitStateChangeListener> = new Set();

  /** Display slot of {@link armId}; what `cliName` fields on events and errors carry. */
  private readonly cliName: CliName;

  constructor(
    private readonly armId: ObservedArmId,
    private readonly config: CircuitBreakerConfig = DEFAULT_CIRCUIT_BREAKER_CONFIG
  ) {
    this.cliName = observedArmDisplaySlot(armId);
    this.lastStateChange = getTimeProvider().now();
  }

  /**
   * Executes a function with circuit breaker protection.
   */
  async execute<T>(fn: () => Promise<T>): Promise<Result<T, CircuitError>> {
    const canExecute = this.canExecute();
    if (!canExecute.ok) {
      return canExecute;
    }

    try {
      const result = await fn();
      this.onSuccess();
      return ok(result);
    } catch (error) {
      const category = categorizeError(error);
      if (this.shouldCountFailure(category)) {
        this.onFailure(category);
      }
      return err(this.createExecutionError(error, category));
    }
  }

  getState(): CircuitState {
    this.checkStateTransition();
    return this.state;
  }

  getSnapshot(): CircuitBreakerSnapshot {
    this.checkStateTransition();
    return {
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      lastFailureTime: this.lastFailureTime,
      lastStateChange: this.lastStateChange,
      halfOpenRequests: this.halfOpenRequests,
      config: this.config,
    };
  }

  reset(): void {
    const previousState = this.state;
    this.state = 'closed';
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = null;
    this.halfOpenRequests = 0;
    this.lastStateChange = getTimeProvider().now();

    if (previousState !== 'closed') {
      this.emitStateChange(previousState, 'closed', 'Manual reset');
    }
  }

  recordFailure(category: FailureCategory): void {
    if (this.shouldCountFailure(category)) {
      this.onFailure(category);
    }
  }

  recordSuccess(): void {
    this.onSuccess();
  }

  addStateChangeListener(listener: CircuitStateChangeListener): void {
    this.listeners.add(listener);
  }

  removeStateChangeListener(listener: CircuitStateChangeListener): void {
    this.listeners.delete(listener);
  }

  // -------------------------------------------------------------------------
  // Private Methods
  // -------------------------------------------------------------------------

  private canExecute(): Result<true, CircuitError> {
    this.checkStateTransition();

    if (this.state === 'closed') {
      return ok(true);
    }

    if (this.state === 'open') {
      return err(
        new CircuitError(`Circuit is open for CLI: ${this.armId}`, {
          circuitErrorCode: CircuitErrorCode.CIRCUIT_OPEN,
          cliName: this.cliName,
          armId: this.armId,
          circuitState: this.state,
        })
      );
    }

    // half-open state
    if (this.halfOpenRequests >= this.config.halfOpenMaxRequests) {
      return err(
        new CircuitError(`Circuit half-open request limit reached for CLI: ${this.armId}`, {
          circuitErrorCode: CircuitErrorCode.CIRCUIT_HALF_OPEN_REJECTED,
          cliName: this.cliName,
          armId: this.armId,
          circuitState: this.state,
        })
      );
    }
    this.halfOpenRequests++;
    return ok(true);
  }

  private checkStateTransition(): void {
    if (this.state === 'open') {
      // #5011: measured from the TRANSITION, not from the last failure. Keyed
      // on `lastFailureTime` the window restarted on every failure recorded
      // while open — and an open circuit does not shed load on the default
      // paths (`base-adapter` and `resilient-adapter` never consult
      // `canExecute`), so traffic kept arriving, kept failing occasionally, and
      // kept pushing the half-open probe another 30s out. A CLI serving 95% of
      // requests correctly stayed evicted from every voter panel until a manual
      // `reset()`. Panel choice: fix the timer, leave load-shedding alone
      // (7-voter panel, Option A, 4 of 5 approvers, audit record #79).
      const elapsed = getTimeProvider().now() - this.lastStateChange;
      if (elapsed >= this.config.resetTimeoutMs) {
        this.transitionTo('half-open', 'Reset timeout elapsed');
      }
    }
  }

  private onSuccess(): void {
    if (this.state === 'closed') {
      this.failureCount = 0;
    } else if (this.state === 'half-open') {
      this.successCount++;
      if (this.successCount >= this.config.halfOpenSuccessThreshold) {
        const reason = `${String(this.successCount)} consecutive successes in half-open state`;
        this.transitionTo('closed', reason);
      }
    }
  }

  private onFailure(category: FailureCategory): void {
    // #5011: a failure recorded while the circuit is already open carries no
    // information — the circuit is open, that is the verdict — and updating
    // `lastFailureTime` here is what delayed recovery.
    //
    // #5034: this line is the LOAD-BEARING half, and the tests now say so:
    // dropping it alone fails two assertions. The `lastStateChange` change in
    // `checkStateTransition` is genuinely redundant given this return —
    // reverting THAT alone fails nothing, because with `lastFailureTime`
    // frozen the two fields are set microseconds apart at the open transition
    // and never diverge. It is kept because measuring recovery from the
    // transition is what the window means, not because any behaviour
    // distinguishes it. Saying which half is pinned, and admitting the other
    // cannot be, beats a mutation table that implies two clean kills.
    //
    // `failureCount` stays put too: it drives the closed-state threshold and
    // is reset on the transition back to closed.
    if (this.state === 'open') return;

    this.failureCount++;
    this.lastFailureTime = getTimeProvider().now();

    if (this.state === 'closed' && this.failureCount >= this.config.failureThreshold) {
      const reason = `Failure threshold (${String(this.config.failureThreshold)}) exceeded`;
      this.transitionTo('open', reason);
    } else if (this.state === 'half-open') {
      this.transitionTo('open', `Failure during half-open recovery (category: ${category})`);
    }
  }

  private transitionTo(newState: CircuitState, reason: string): void {
    const previousState = this.state;
    this.state = newState;
    this.lastStateChange = getTimeProvider().now();

    if (newState === 'closed') {
      this.failureCount = 0;
      this.successCount = 0;
      this.halfOpenRequests = 0;
    } else if (newState === 'half-open') {
      // #3026 finding 5: pre-fix `failureCount` carried over across
      // recovery cycles. transitionTo('open',…) only zeroes counts on
      // 'closed', so a half-open→open→half-open flaky pattern grew
      // `failureCount` monotonically across cycles. The count served
      // its threshold purpose when we opened the circuit; resetting on
      // 'half-open' restores the documented per-cycle semantic that
      // operator dashboards / alerts read from `getSnapshot()`.
      this.failureCount = 0;
      this.successCount = 0;
      this.halfOpenRequests = 0;
    }

    this.emitStateChange(previousState, newState, reason);
  }

  private emitStateChange(
    previousState: CircuitState,
    newState: CircuitState,
    reason: string
  ): void {
    const event: CircuitStateChangeEvent = {
      cliName: this.cliName,
      armId: this.armId,
      previousState,
      newState,
      timestamp: this.lastStateChange,
      failureCount: this.failureCount,
      reason,
    };

    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch {
        // Ignore listener errors to prevent cascade
      }
    }
  }

  private shouldCountFailure(category: FailureCategory): boolean {
    if (category === 'timeout') return this.config.countTimeoutsAsFailures;
    if (category === 'authentication') return this.config.countAuthFailuresAsFailures;
    if (category === 'rate_limit') return this.config.countRateLimitsAsFailures;
    return true;
  }

  private createExecutionError(error: unknown, category: FailureCategory): CircuitError {
    const message = getErrorMessage(error);
    const cause = error instanceof Error ? error : new Error(String(error));
    return new CircuitError(`CLI execution failed: ${message}`, {
      circuitErrorCode: CircuitErrorCode.EXECUTION_FAILED,
      cliName: this.cliName,
      armId: this.armId,
      circuitState: this.state,
      failureCategory: category,
      cause,
    });
  }
}

// ============================================================================
// Registry
// ============================================================================

/**
 * Registry for managing per-arm circuit breakers.
 *
 * Keyed by {@link ObservedArmId} since #4392 — a published `RoutingArmId` or a
 * registered `EndpointArmId`: a CLI slot and an `api:*` arm are distinct keys,
 * so an endpoint opening never speaks for the CLI slot it displays under. The `*Arm*` methods are the source of truth over that map;
 * the older `CliName`-typed methods keep their signatures and are FILTERED
 * VIEWS of the same map, restricted to the four CLI slots — an `api:*` arm
 * never appears in `getHealthyClis()` / `getUnhealthyClis()` /
 * `getAllSnapshots()`, only in their `*Arms` siblings (#6290 panel; the
 * narrow methods are removed in 9.0, #6291).
 */
export class CircuitBreakerRegistry {
  private readonly breakers: Map<ObservedArmId, CliCircuitBreaker> = new Map();
  private readonly globalListeners: Set<CircuitStateChangeListener> = new Set();

  constructor(private readonly defaultConfig: Partial<CircuitBreakerConfig> = {}) {}

  /** Breaker for any routing arm, created on first request (#4392). */
  getArmBreaker(armId: ObservedArmId, config?: Partial<CircuitBreakerConfig>): CliCircuitBreaker {
    let breaker = this.breakers.get(armId);

    if (!breaker) {
      const mergedConfig: CircuitBreakerConfig = {
        ...DEFAULT_CIRCUIT_BREAKER_CONFIG,
        ...this.defaultConfig,
        ...config,
      };
      breaker = new CliCircuitBreaker(armId, mergedConfig);

      for (const listener of this.globalListeners) {
        breaker.addStateChangeListener(listener);
      }

      this.breakers.set(armId, breaker);
    }

    return breaker;
  }

  /** CLI-slot view of {@link getArmBreaker}: the same breaker, same map. */
  getBreaker(cliName: CliName, config?: Partial<CircuitBreakerConfig>): CliCircuitBreaker {
    return this.getArmBreaker(cliName, config);
  }

  isArmOpen(armId: ObservedArmId): boolean {
    return this.breakers.get(armId)?.getState() === 'open';
  }

  /** CLI-slot view of {@link isArmOpen}. */
  isOpen(cliName: CliName): boolean {
    return this.isArmOpen(cliName);
  }

  /** Snapshot of every arm the registry holds, CLI slots and `api:*` arms alike. */
  getAllArmSnapshots(): Map<ObservedArmId, CircuitBreakerSnapshot> {
    const snapshots = new Map<ObservedArmId, CircuitBreakerSnapshot>();
    for (const [name, breaker] of this.breakers) {
      snapshots.set(name, breaker.getSnapshot());
    }
    return snapshots;
  }

  /** CLI-slot view of {@link getAllArmSnapshots}: `api:*` arms are filtered out. */
  getAllSnapshots(): Map<CliName, CircuitBreakerSnapshot> {
    const snapshots = new Map<CliName, CircuitBreakerSnapshot>();
    for (const [name, snapshot] of this.getAllArmSnapshots()) {
      if (isCliName(name)) snapshots.set(name, snapshot);
    }
    return snapshots;
  }

  resetAll(): void {
    for (const breaker of this.breakers.values()) {
      breaker.reset();
    }
  }

  resetArm(armId: ObservedArmId): void {
    this.breakers.get(armId)?.reset();
  }

  /** CLI-slot view of {@link resetArm}. */
  reset(cliName: CliName): void {
    this.resetArm(cliName);
  }

  addGlobalStateChangeListener(listener: CircuitStateChangeListener): void {
    this.globalListeners.add(listener);
    for (const breaker of this.breakers.values()) {
      breaker.addStateChangeListener(listener);
    }
  }

  removeGlobalStateChangeListener(listener: CircuitStateChangeListener): void {
    this.globalListeners.delete(listener);
    for (const breaker of this.breakers.values()) {
      breaker.removeStateChangeListener(listener);
    }
  }

  /** Every arm whose circuit is closed. Empty registry: `[]` — nothing is known healthy. */
  getHealthyArms(): ObservedArmId[] {
    const healthy: ObservedArmId[] = [];
    for (const [name, breaker] of this.breakers) {
      if (breaker.getState() === 'closed') {
        healthy.push(name);
      }
    }
    return healthy;
  }

  /** CLI-slot view of {@link getHealthyArms}: `api:*` arms are filtered out. */
  getHealthyClis(): CliName[] {
    return this.getHealthyArms().filter(isCliName);
  }

  /** Every arm whose circuit is open or half-open. Empty registry: `[]`. */
  getUnhealthyArms(): ObservedArmId[] {
    const unhealthy: ObservedArmId[] = [];
    for (const [name, breaker] of this.breakers) {
      const state = breaker.getState();
      if (state === 'open' || state === 'half-open') {
        unhealthy.push(name);
      }
    }
    return unhealthy;
  }

  /** CLI-slot view of {@link getUnhealthyArms}: `api:*` arms are filtered out. */
  getUnhealthyClis(): CliName[] {
    return this.getUnhealthyArms().filter(isCliName);
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Maps CLI error codes to failure categories.
 */
export function mapCliErrorToCategory(errorCode: CliErrorCode): FailureCategory {
  const mapping: Record<string, FailureCategory> = {
    TIMEOUT: 'timeout',
    NOT_AUTHENTICATED: 'authentication',
    RATE_LIMITED: 'rate_limit',
    CONNECTION_ERROR: 'connection',
  };
  return mapping[errorCode] ?? 'unknown';
}

/**
 * Maps a direct-API {@link ModelError} to a circuit-breaker failure category.
 *
 * Mirrors {@link mapCliErrorToCategory} for the IModelAdapter layer (#3423):
 * API failures must drive the same breaker degradation/failover learning that
 * CLI subprocess failures already get. Maps by the structured `error.code`
 * first, then falls back to message-pattern categorization via
 * {@link categorizeError} (which returns `'unknown'` if nothing matches).
 *
 * The fallback is what turns a generic `MODEL_ERROR` whose message reads
 * "connection refused" into a `connection` category instead of `unknown`.
 */
export function mapModelErrorToCategory(error: ModelError): FailureCategory {
  switch (error.code) {
    case ErrorCode.MODEL_TIMEOUT:
    case ErrorCode.TIMEOUT_ERROR:
      return 'timeout';
    case ErrorCode.UNAUTHORIZED:
      return 'authentication';
    case ErrorCode.MODEL_RATE_LIMITED:
    case ErrorCode.RATE_LIMIT_ERROR:
      return 'rate_limit';
    case ErrorCode.MODEL_UNAVAILABLE:
    case ErrorCode.MODEL_NOT_FOUND:
      // Intentional reuse of `connection`: there is no dedicated "endpoint gone /
      // model retired" category, and `connection` is a counted, failover-driving
      // category — the right behaviour for an unavailable/missing model.
      return 'connection';
    default:
      // MODEL_ERROR and any other code: fall back to message-pattern matching.
      return categorizeError(error);
  }
}

// `createCircuitBreakerRegistryWithMetrics` and the
// `Capacity Monitor Integration` section
// (`integrateCapacityMonitorWithCircuitBreaker` + the
// `CapacityMonitorIntegrationConfig` interface and its default config)
// were removed in #3018 — both had only test-file callers in the tree.
// The `CircuitBreakerRegistry` above is what production adapters use;
// if metrics-logging integration comes back as a real requirement,
// reintroduce it alongside the consumer code in the same PR
// (activation-or-delete YAGNI — same pattern as #2937–#2940).
//
// The capacity-monitor half of that sentence is gone: the provider-header
// monitor it referred to was removed in #4532 after 7 months unwired, so
// there is nothing to "come back". Header parsing is now a BUILD.
